import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RefreshCwIcon, UsersIcon, TargetIcon, ActivityIcon } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function PMAgilePage() {
  return <div className="w-full" data-id="element-2009" data-mp="3f59czmww23iq">
      {/* HERO SECTION */}
      <section className="section-navy hero-grid-pattern py-12" data-id="element-2010" data-mp="3a53scqp1dha5">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2011" data-mp="35ddeiaqw4bdz">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-2012" data-mp="35b8zoxrgdryx">
            <div className="text-sm font-bold text-nrs-light mb-4 uppercase tracking-wider" data-id="element-2013" data-mp="31cjmp6abucft">
              <Link to="/services" className="hover:text-nrs-orange" data-id="element-2014" data-mp="2wdymh6v2rsrd">
                Services
              </Link>{' '}
              &gt;{' '}
              <Link to="/services/program-and-project-management" className="hover:text-nrs-orange" data-id="element-2015" data-mp="2mehjwyb4duzm">
                Program Management
              </Link>{' '}
              &gt; Agile
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight" data-id="element-2016" data-mp="hbgquf04jrwi">
              Agile Program Management Services
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange rounded-full mb-6" data-id="element-2017" data-mp="2nvnql391sol"></div>
          </motion.div>
        </div>
      </section>

      {/* AGILE APPROACH OVERVIEW */}
      <section className="section-white border-b border-gray-200" data-id="element-2018" data-mp="vbht9yw1f7e3">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2019" data-mp="v958zf2t4kxv">
          <h2 className="text-3xl font-bold mb-8" data-id="element-2020" data-mp="109szlzn4izrk">
            Our Agile Delivery Approach
          </h2>
          <div className="max-w-4xl space-y-6 text-gray-600 leading-relaxed text-lg" data-id="element-2021" data-mp="lciud812qq4a">
            <p data-id="element-2022" data-mp="qatyr38lo8f4">
              Agile methodologies have transformed federal IT delivery, shifting
              the focus from rigid, multi-year plans to iterative development
              and continuous value delivery. However, implementing Agile in
              federal environments—especially secure ones—requires specialized
              expertise to balance agility with strict security and acquisition
              requirements.
            </p>
            <p data-id="element-2023" data-mp="17tkfdic0at29">
              New River Systems provides certified Agile practitioners who
              understand how to tailor Scrum, Kanban, and SAFe (Scaled Agile
              Framework) to work within the constraints of DoD and federal
              environments. We don't just "do Agile"; we build Agile cultures
              that deliver mission-critical software faster and with higher
              quality.
            </p>
            <p data-id="element-2024" data-mp="ow15ydlmz8fb">
              Whether you need a single Scrum Master for a new development team
              or an Agile Release Train Engineer to coordinate a 100-person
              program, our cleared professionals have the experience to lead
              your Agile transformation.
            </p>
          </div>
        </div>
      </section>

      {/* AGILE ROLES WE PROVIDE */}
      <section className="section-light-blue" data-id="element-2025" data-mp="2lu71hr3k7e9d">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2026" data-mp="2lrcgnygmvrff">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-2027" data-mp="2qr75zxkrg8yd">
            Agile Program Management Roles
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-2028" data-mp="2vlbh8t127359"></div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2029" data-mp="30hs6jqjzvnjx">
            <div className="card flex flex-col h-full border-t-4 border-t-nrs-navy" data-id="element-2030" data-mp="22vcoamjqulvb">
              <RefreshCwIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-2031" data-mp="27t4d37p3drva" />
              <h3 className="text-xl font-bold mb-4" data-id="element-2032" data-mp="27vh2u713d6iw">Scrum Master</h3>
              <div className="mb-6 flex-grow" data-id="element-2033" data-mp="2cr5yhz9zq39x">
                <h4 className="font-bold text-nrs-navy mb-2" data-id="element-2034" data-mp="2hrk83lr8pla2">
                  Responsibilities:
                </h4>
                <ul className="space-y-2 text-sm text-gray-600 list-disc ml-4" data-id="element-2035" data-mp="3o8p4u0ivamcp">
                  <li data-id="element-2036" data-mp="3t6qr0ublb1se">
                    Facilitate Scrum ceremonies (sprint planning, daily
                    standups, retros, demos)
                  </li>
                  <li data-id="element-2037" data-mp="3queqm38scvx5">Remove impediments and blockers</li>
                  <li data-id="element-2038" data-mp="3mrs66neg2d9z">Coach team on Agile practices</li>
                  <li data-id="element-2039" data-mp="3mt362c4rr0vt">Track velocity and sprint metrics</li>
                  <li data-id="element-2040" data-mp="4lx3jmbbltgn">Shield team from external distractions</li>
                </ul>
              </div>
              <div className="bg-nrs-light p-3 rounded-md text-sm" data-id="element-2041" data-mp="3vpu2wqr5ajrg">
                <span className="font-bold text-nrs-navy" data-id="element-2042" data-mp="dl0ice3gfmpc">Certifications:</span>{' '}
                CSM, PSM, SAFe Scrum Master
              </div>
            </div>

            <div className="card flex flex-col h-full border-t-4 border-t-nrs-orange" data-id="element-2043" data-mp="nblrgvgr89mi">
              <TargetIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-2044" data-mp="ndg1vfuegjcc" />
              <h3 className="text-xl font-bold mb-4" data-id="element-2045" data-mp="id254ty90vtm">Product Owner</h3>
              <div className="mb-6 flex-grow" data-id="element-2046" data-mp="x8zxe151xg0k">
                <h4 className="font-bold text-nrs-navy mb-2" data-id="element-2047" data-mp="x7ostz3s848e">
                  Responsibilities:
                </h4>
                <ul className="space-y-2 text-sm text-gray-600 list-disc ml-4" data-id="element-2048" data-mp="1bwi3n3y9iip3">
                  <li data-id="element-2049" data-mp="1budotqytrza1">Own and prioritize product backlog</li>
                  <li data-id="element-2050" data-mp="1fx20s7iu5mb8">Define acceptance criteria and user stories</li>
                  <li data-id="element-2051" data-mp="11ykduc0r8dud">Stakeholder engagement and requirements gathering</li>
                  <li data-id="element-2052" data-mp="120ogkqmx36t2">Sprint goal definition</li>
                  <li data-id="element-2053" data-mp="16wc8vewp4g2s">Demo preparation and stakeholder communication</li>
                </ul>
              </div>
              <div className="bg-nrs-light p-3 rounded-md text-sm" data-id="element-2054" data-mp="s7pkknp3cdzf">
                <span className="font-bold text-nrs-navy" data-id="element-2055" data-mp="wzqysf5tkes3">Certifications:</span>{' '}
                CSPO, SAFe POPM
              </div>
            </div>

            <div className="card flex flex-col h-full border-t-4 border-t-nrs-navy" data-id="element-2056" data-mp="j5q0fmis4ts9">
              <UsersIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-2057" data-mp="o75l3dh1oya4" />
              <h3 className="text-xl font-bold mb-4" data-id="element-2058" data-mp="9aoawpkldpp5">
                Agile Program Manager / RTE
              </h3>
              <div className="mb-6 flex-grow" data-id="element-2059" data-mp="e7esxifyivd7">
                <h4 className="font-bold text-nrs-navy mb-2" data-id="element-2060" data-mp="3r9imkfa4zuv5">
                  Responsibilities:
                </h4>
                <ul className="space-y-2 text-sm text-gray-600 list-disc ml-4" data-id="element-2061" data-mp="3mi8yvk85f1lw">
                  <li data-id="element-2062" data-mp="3hjft6dz3sewa">Coordinate multiple Scrum teams (Agile Release Train)</li>
                  <li data-id="element-2063" data-mp="3hhctf285f7ed">PI (Program Increment) Planning facilitation</li>
                  <li data-id="element-2064" data-mp="3cm5us8tqkswb">Cross-team dependency management</li>
                  <li data-id="element-2065" data-mp="e3ihg0lamyvu">Program-level risk and issue tracking</li>
                  <li data-id="element-2066" data-mp="e0w75t70q59x">Value stream mapping and metrics</li>
                </ul>
              </div>
              <div className="bg-nrs-light p-3 rounded-md text-sm" data-id="element-2067" data-mp="91t1q7n4lx1f">
                <span className="font-bold text-nrs-navy" data-id="element-2068" data-mp="43hrvwwtzmj7">Certifications:</span>{' '}
                SAFe RTE, SAFe SPC
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SAFE IMPLEMENTATION */}
      <section className="section-white border-b border-gray-200" data-id="element-2069" data-mp="2osnp6ubsqlye">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2070" data-mp="2judsugec5fb7">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2071" data-mp="zeo44rqt72x8">
            <div data-id="element-2072" data-mp="14cpvs12awakb">
              <h2 className="text-3xl font-bold mb-6" data-id="element-2073" data-mp="14c7v8s8lva6l">
                Scaled Agile Framework (SAFe) Expertise
              </h2>
              <p className="text-lg text-gray-600 mb-8" data-id="element-2074" data-mp="19ahlcofh3klc">
                For large, complex programs with 50+ team members, we implement
                SAFe to coordinate multiple Agile teams and align development
                with agency strategy.
              </p>
              <div className="bg-nrs-light p-6 rounded-lg border border-nrs-blue/20" data-id="element-2075" data-mp="1n2fqyp9z6ycr">
                <h3 className="text-lg font-bold text-nrs-navy mb-4" data-id="element-2076" data-mp="1s2212puibyhc">
                  What We Deliver:
                </h3>
                <ul className="space-y-3 text-gray-700" data-id="element-2077" data-mp="1x0c23h2wxtav">
                  <li className="flex items-start gap-2" data-id="element-2078" data-mp="3tu6te2fx4xsl">
                    <span className="text-nrs-orange font-bold" data-id="element-2079" data-mp="3tvrl7kaum2mr">•</span> SAFe
                    maturity assessment and roadmap
                  </li>
                  <li className="flex items-start gap-2" data-id="element-2080" data-mp="24knxb3tvx48z">
                    <span className="text-nrs-orange font-bold" data-id="element-2081" data-mp="24j33xhjb3rcp">•</span> Agile
                    Release Train (ART) setup
                  </li>
                  <li className="flex items-start gap-2" data-id="element-2082" data-mp="2eb8uqnxto2qb">
                    <span className="text-nrs-orange font-bold" data-id="element-2083" data-mp="2ectr8j3p5vqt">•</span> PI
                    Planning facilitation (quarterly, in-person)
                  </li>
                  <li className="flex items-start gap-2" data-id="element-2084" data-mp="1ln3ae2aokfr0">
                    <span className="text-nrs-orange font-bold" data-id="element-2085" data-mp="1gngx5suuqzi7">•</span> Program
                    backlog management
                  </li>
                  <li className="flex items-start gap-2" data-id="element-2086" data-mp="1vl9dgn883rxg">
                    <span className="text-nrs-orange font-bold" data-id="element-2087" data-mp="1qloe4bp55hk7">•</span> Lean
                    Portfolio Management (if needed)
                  </li>
                  <li className="flex items-start gap-2" data-id="element-2088" data-mp="36xtm3oeasetv">
                    <span className="text-nrs-orange font-bold" data-id="element-2089" data-mp="321cxyu73m0gs">•</span> DevOps
                    and continuous delivery pipeline integration
                  </li>
                </ul>
              </div>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl border border-gray-200 h-full flex flex-col justify-center text-center" data-id="element-2090" data-mp="3ejys92mx0w5s">
              <ActivityIcon className="h-16 w-16 text-nrs-orange mx-auto mb-6" data-id="element-2091" data-mp="30lhbjovfgjxd" />
              <h3 className="text-2xl font-bold text-nrs-navy mb-4" data-id="element-2092" data-mp="34lhy5wsok1fi">
                Metrics and Transparency
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-2093" data-mp="3scwvfb5lnsjw">
                We track the metrics that matter to ensure predictable delivery:
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm text-left" data-id="element-2094" data-mp="3jh7fhhmx7qdw">
                <div className="bg-white p-3 rounded shadow-sm border border-gray-100" data-id="element-2095" data-mp="3njlu1pjk43zd">
                  Velocity (story points per sprint)
                </div>
                <div className="bg-white p-3 rounded shadow-sm border border-gray-100" data-id="element-2096" data-mp="f1xszxhv92er">
                  Sprint burn-down charts
                </div>
                <div className="bg-white p-3 rounded shadow-sm border border-gray-100" data-id="element-2097" data-mp="2dnsb13ebkdon">
                  Release burn-up charts
                </div>
                <div className="bg-white p-3 rounded shadow-sm border border-gray-100" data-id="element-2098" data-mp="2il254wcnldit">
                  Cumulative flow diagrams
                </div>
                <div className="bg-white p-3 rounded shadow-sm border border-gray-100" data-id="element-2099" data-mp="28nxk4zujno3l">
                  Cycle time and lead time
                </div>
                <div className="bg-white p-3 rounded shadow-sm border border-gray-100" data-id="element-2100" data-mp="1uvpojbezy36h">
                  Defect escape rate
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AGILE IN CLASSIFIED ENVIRONMENTS */}
      <section className="section-white bg-gray-50" data-id="element-2101" data-mp="1pyqx50mpwabz">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2102" data-mp="1by6fi7sjgkk2">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-2103" data-mp="1g0a31ushfflp">
            Agile Delivery in Secure Government Environments
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-2104" data-mp="xd0wnxnuukfz"></div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden max-w-4xl mx-auto" data-id="element-2105" data-mp="sdfw5it1e415">
            <table className="w-full text-left border-collapse" data-id="element-2106" data-mp="16dqokq9zvqof">
              <thead data-id="element-2107" data-mp="11dkwj8ztc25t">
                <tr className="bg-nrs-navy text-white" data-id="element-2108" data-mp="11dbcs35d2r8k">
                  <th className="p-4 font-bold w-1/2" data-id="element-2109" data-mp="11fxkc2s956qs">Challenge</th>
                  <th className="p-4 font-bold w-1/2" data-id="element-2110" data-mp="1g9ajb9ctojbl">Our Solution</th>
                </tr>
              </thead>
              <tbody className="text-gray-700" data-id="element-2111" data-mp="1bbighkan3csm">
                <tr className="border-b border-gray-200" data-id="element-2112" data-mp="1p3own6dsljs2">
                  <td className="p-4 bg-gray-50" data-id="element-2113" data-mp="1p59q0sodewoc">
                    Limited collaboration tools in secure networks
                  </td>
                  <td className="p-4" data-id="element-2114" data-mp="1z0tmphardze5">
                    Physical boards, approved wikis, manual tracking
                  </td>
                </tr>
                <tr className="border-b border-gray-200" data-id="element-2115" data-mp="1u435gqn7wxr5">
                  <td className="p-4 bg-gray-50" data-id="element-2116" data-mp="rpn7n64jeoj9">
                    Distributed teams across secure facilities
                  </td>
                  <td className="p-4" data-id="element-2117" data-mp="vqp81qa6x6mt">
                    Video teleconference stand-ups, in-person PI Planning
                  </td>
                </tr>
                <tr className="border-b border-gray-200" data-id="element-2118" data-mp="15gii2muopdoy">
                  <td className="p-4 bg-gray-50" data-id="element-2119" data-mp="15i3d0dkwuync">
                    Air-gapped development environments
                  </td>
                  <td className="p-4" data-id="element-2120" data-mp="1fdwyimgly2h9">
                    On-site Scrum Masters, localized CI/CD pipelines
                  </td>
                </tr>
                <tr data-id="element-2121" data-mp="1jgt9bqzwouan">
                  <td className="p-4 bg-gray-50" data-id="element-2122" data-mp="1je74hz4egsw4">
                    Security classification of artifacts
                  </td>
                  <td className="p-4" data-id="element-2123" data-mp="3l7wjkf5vw6uo">
                    Daily classification reviews, spillage prevention training
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-navy" data-id="element-2124" data-mp="3iorukn1epn14">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-2125" data-mp="3dvyonvu7c8ng">
          <h3 className="text-3xl font-bold mb-8 text-white" data-id="element-2126" data-mp="38wvsz2043es5">
            Ready to Accelerate Your Federal IT Program with Agile?
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2127" data-mp="33zndj96cloul">
            <Link to="/contact-us" className="btn-primary" data-id="element-2128" data-mp="5fdnv2bq4lvf">
              Discuss Agile Program Management
            </Link>
            <Link to="/services/program-and-project-management" className="btn-secondary border-white text-white hover:bg-white/10 hover:text-white" data-id="element-2129" data-mp="hvghgdcuepj">
              Back to Program Management
            </Link>
          </div>
        </div>
      </section>
    </div>;
}